import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { adminApi, type AdminStatisticsData, type AdminShareBookStats } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Users, Building2, FileText, Wallet, RefreshCw, TrendingDown, AlertTriangle, UserX, DollarSign, TrendingUp, Target, BookOpen, Lock, ArrowRightLeft } from "lucide-react";
import {
  StatCard,
  MetricGrid,
  ChartCard,
  DateRangeSelector,
  AreaChartWidget,
  BarChartWidget,
  DonutChartWidget,
  type Period,
} from "@/components/charts";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

// ═══════════════════════════════════════════════════════════════════
// Colors
// ═══════════════════════════════════════════════════════════════════

const COLORS = {
  users: "#0284c7",
  companies: "#059669",
  reports: "#d97706",
  revenue: "#7c3aed",
};

// ═══════════════════════════════════════════════════════════════════
// Formatters
// ═══════════════════════════════════════════════════════════════════

function formatCurrency(value: number | string): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toLocaleString("sv-SE", { maximumFractionDigits: 1 })} MSEK`;
  }
  if (num >= 10_000) {
    return `${(num / 1_000).toLocaleString("sv-SE", { maximumFractionDigits: 0 })} TSEK`;
  }
  return `${num.toLocaleString("sv-SE")} kr`;
}

function formatNumber(value: number | string): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return num.toLocaleString("sv-SE");
}

function formatBucketLabel(dateStr: string, bucketSize: "day" | "week" | "month"): string {
  const date = new Date(dateStr);
  switch (bucketSize) {
    case "day":
      return date.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
    case "week":
      return `v${getWeekNumber(date)}`;
    case "month":
      return date.toLocaleDateString("sv-SE", { month: "short", year: "2-digit" });
  }
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function formatYValue(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value.toString();
}

// ═══════════════════════════════════════════════════════════════════
// Data transformers
// ═══════════════════════════════════════════════════════════════════

type TimeSeriesRow = {
  [key: string]: string | number;
  bucket: string;
  newUsers: number;
  newCompanies: number;
  newReports: number;
  revenue: number;
};

function buildTimeSeriesData(ts: AdminStatisticsData["timeSeries"]): TimeSeriesRow[] {
  return ts.buckets.map((bucket, i) => ({
    bucket,
    newUsers: ts.newUsers[i] ?? 0,
    newCompanies: ts.newCompanies[i] ?? 0,
    newReports: ts.newReports[i] ?? 0,
    revenue: ts.revenue[i] ?? 0,
  }));
}

type FunnelRow = {
  [key: string]: string | number;
  step: string;
  count: number;
};

// ═══════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════

export function AdminStatistics() {
  const [data, setData] = useState<AdminStatisticsData | null>(null);
  const [shareBookStats, setShareBookStats] = useState<AdminShareBookStats | null>(null);
  const [period, setPeriod] = useState<Period>("30d");
  const [customRange, setCustomRange] = useState<{ from: Date; to: Date } | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatistics = async (p: Period, customDates?: { from: Date; to: Date }) => {
    // First load: full spinner. Subsequent: subtle refresh indicator.
    if (data) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      // Build custom range params if period is custom
      // Use local date formatting (not toISOString which converts to UTC first)
      const formatLocalDate = (d: Date) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
      const customRangeParams = p === "custom" && customDates
        ? { from: formatLocalDate(customDates.from), to: formatLocalDate(customDates.to) }
        : undefined;

      // Fetch both statistics and share book stats in parallel
      const [res, shareBookRes] = await Promise.all([
        adminApi.getStatistics(p, customRangeParams),
        adminApi.getShareBookStats(),
      ]);

      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError(res.error?.message || "Kunde inte hämta statistik");
      }

      if (shareBookRes.success && shareBookRes.data) {
        setShareBookStats(shareBookRes.data);
      }
    } catch {
      setError("Kunde inte ansluta till servern");
      toast.error("Kunde inte ladda statistik");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (period === "custom" && customRange) {
      loadStatistics(period, customRange);
    } else if (period !== "custom") {
      loadStatistics(period);
    }
  }, [period, customRange]);

  const timeSeriesData = useMemo(
    () => (data ? buildTimeSeriesData(data.timeSeries) : []),
    [data]
  );

  const funnelData = useMemo<FunnelRow[]>(
    () => (data ? data.distributions.reportFunnel : []),
    [data]
  );

  // Initial load — full-page spinner (centered both horizontally and vertically)
  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error}</p>
        <button
          onClick={() => loadStatistics(period)}
          className="mt-3 text-sm text-red-600 hover:underline"
        >
          Försök igen
        </button>
      </div>
    );
  }

  if (!data) return null;

  const bucketSize = data.meta.bucketSize;

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.statistics.title} noIndex />
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Statistik</h1>
            <p className="text-gray-600 mt-1">
              {data.meta.periodStart && data.meta.periodEnd
                ? `${new Date(data.meta.periodStart).toLocaleDateString("sv-SE")} – ${new Date(data.meta.periodEnd).toLocaleDateString("sv-SE")}`
                : "Översikt"}
            </p>
          </div>
          {isRefreshing && (
            <RefreshCw size={18} className="text-gray-400 animate-spin" />
          )}
        </div>
        <DateRangeSelector
          value={period}
          onChange={setPeriod}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />
      </div>

      {/* Content wrapper — subtle opacity during refresh */}
      <div className={isRefreshing ? "opacity-60 pointer-events-none transition-opacity" : "transition-opacity"}>
        {/* KPI Cards */}
        <MetricGrid columns={4}>
          <StatCard
            label="Användare"
            value={data.kpis.totalUsers}
            icon={Users}
            iconBg="bg-sky-100"
            iconColor="text-sky-600"
            trend={data.kpis.usersTrend}
            formatValue={formatNumber}
            href="/admin/users"
          />
          <StatCard
            label="Företag"
            value={data.kpis.totalCompanies}
            icon={Building2}
            iconBg="bg-emerald-100"
            iconColor="text-emerald-600"
            trend={data.kpis.companiesTrend}
            formatValue={formatNumber}
            href="/admin/companies"
          />
          <StatCard
            label="Årsredovisningar"
            value={data.kpis.totalReports}
            icon={FileText}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
            trend={data.kpis.reportsTrend}
            formatValue={formatNumber}
            href="/admin/reports"
          />
          <StatCard
            label="Intäkter"
            value={data.kpis.totalRevenue}
            icon={Wallet}
            iconBg="bg-violet-100"
            iconColor="text-violet-600"
            trend={data.kpis.revenueTrend}
            formatValue={formatCurrency}
            href="/admin/subscriptions"
          />
        </MetricGrid>

        {/* Conversion, Churn & Revenue Metrics */}
        {(data.conversionMetrics || data.revenueMetrics) && (
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Conversion Metrics */}
              {data.conversionMetrics && (
                <>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <UserX className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Ej aktiverade</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatNumber(data.conversionMetrics.companiesWithoutSubscription)}
                        </p>
                        <p className="text-xs text-gray-400">
                          av {formatNumber(data.kpis.totalCompanies)} företag
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Users className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Aktiveringsgrad</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {data.kpis.totalCompanies > 0
                            ? Math.round(((data.kpis.totalCompanies - data.conversionMetrics.companiesWithoutSubscription) / data.kpis.totalCompanies) * 100)
                            : 0}%
                        </p>
                        <p className="text-xs text-gray-400">
                          företag med prenumeration
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <TrendingDown className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Churn Rate</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {data.conversionMetrics.churnRate}%
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatNumber(data.conversionMetrics.cancelledSubscriptions)} av {formatNumber(data.conversionMetrics.totalSubscriptions)} avslutade
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <AlertTriangle className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Inaktiva rapporter</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatNumber(data.conversionMetrics.staleReports)}
                        </p>
                        <p className="text-xs text-gray-400">
                          ej exporterade &gt;7 dagar
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Revenue Metrics */}
              {data.revenueMetrics && (
                <>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-violet-100 rounded-lg">
                        <DollarSign className="h-5 w-5 text-violet-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">MRR</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(data.revenueMetrics.mrr)}
                        </p>
                        <p className="text-xs text-gray-400">
                          Månatlig återkommande intäkt
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">ARR</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(data.revenueMetrics.arr)}
                        </p>
                        <p className="text-xs text-gray-400">
                          Årlig återkommande intäkt
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Snittintäkt/företag</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(data.revenueMetrics.avgRevenuePerCompany)}
                        </p>
                        <p className="text-xs text-gray-400">
                          genomsnittlig intäkt
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <Target className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">LTV</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(data.revenueMetrics.ltv)}
                        </p>
                        <p className="text-xs text-gray-400">
                          uppskattat livstidsvärde
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Time series charts — 2 columns on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <ChartCard
            title="Tillväxt"
            description="Nya användare & företag per period"
          >
            <AreaChartWidget<TimeSeriesRow>
              data={timeSeriesData}
              xKey="bucket"
              series={[
                { dataKey: "newUsers", label: "Användare", color: COLORS.users },
                {
                  dataKey: "newCompanies",
                  label: "Företag",
                  color: COLORS.companies,
                  fillOpacity: 0.05,
                },
              ]}
              xFormatter={(v) => formatBucketLabel(v, bucketSize)}
              yFormatter={formatYValue}
            />
          </ChartCard>

          <ChartCard
            title="Intäkter"
            description="Intäkter per period"
          >
            <BarChartWidget<TimeSeriesRow>
              data={timeSeriesData}
              xKey="bucket"
              series={[
                { dataKey: "revenue", label: "Intäkter", color: COLORS.revenue },
              ]}
              xFormatter={(v) => formatBucketLabel(v, bucketSize)}
              yFormatter={formatYValue}
            />
          </ChartCard>
        </div>

        {/* Distribution donuts — 3 columns on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          <ChartCard title="Företagstyper" minHeight={280}>
            <DonutChartWidget
              data={data.distributions.companyTypes}
              colors={["#0ea5e9", "#10b981", "#f59e0b", "#f43f5e"]}
              formatValue={formatNumber}
              height={260}
            />
          </ChartCard>

          <ChartCard title="Rapportstatus" minHeight={280}>
            <DonutChartWidget
              data={data.distributions.reportStatuses}
              colors={["#94a3b8", "#0ea5e9", "#10b981", "#8b5cf6"]}
              formatValue={formatNumber}
              height={260}
            />
          </ChartCard>

          <ChartCard title="Aktiveringsstatus" minHeight={280}>
            <DonutChartWidget
              data={data.distributions.subscriptionStatuses}
              colors={["#10b981", "#f59e0b", "#ef4444", "#94a3b8"]}
              formatValue={formatNumber}
              height={260}
            />
          </ChartCard>
        </div>

        {/* Report funnel — full width */}
        {funnelData.length > 0 && (
          <div className="mt-8">
            <ChartCard
              title="Rapporttratt"
              description="Steg i wizarden"
              minHeight={Math.max(200, funnelData.length * 48 + 40)}
            >
              <BarChartWidget<FunnelRow>
                data={funnelData}
                xKey="step"
                series={[{ dataKey: "count", label: "Antal", color: COLORS.users }]}
                layout="horizontal"
                yFormatter={formatYValue}
                height={Math.max(200, funnelData.length * 48 + 40)}
                radius={6}
              />
            </ChartCard>
          </div>
        )}

        {/* Share Book Stats */}
        {shareBookStats && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BookOpen size={20} className="text-primary-600" />
              Aktieböcker
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <BookOpen className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Totalt aktieböcker</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(shareBookStats.totalShareBooks)}
                    </p>
                    <p className="text-xs text-gray-400">
                      registrerade i systemet
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Lock className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Låsta aktieböcker</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(shareBookStats.lockedShareBooks)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {shareBookStats.totalShareBooks > 0
                        ? `${Math.round((shareBookStats.lockedShareBooks / shareBookStats.totalShareBooks) * 100)}% av totalt`
                        : "0%"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Aktieägare</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(shareBookStats.activeShareholders)}
                    </p>
                    <p className="text-xs text-gray-400">
                      av {formatNumber(shareBookStats.totalShareholders)} totalt
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ArrowRightLeft className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Transaktioner</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(shareBookStats.totalTransactions)}
                    </p>
                    <p className="text-xs text-gray-400">
                      aktieöverlåtelser registrerade
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Share Capital Summary */}
            <div className="mt-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg border border-primary-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg">
                    <Wallet className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm text-primary-700 font-medium">Totalt aktiekapital</p>
                    <p className="text-2xl font-bold text-primary-900">
                      {formatCurrency(shareBookStats.totalShareCapital)}
                    </p>
                  </div>
                </div>
                <a
                  href="/admin/share-books"
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline"
                >
                  Visa alla aktieböcker →
                </a>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
    </>
  );
}
